import { Box, Button, Chip, Typography } from '@mui/material'
import {
  DataGridPro,
  GridAlignment,
  GridColDef,
  GridEventListener,
  GridFooter,
  GridFooterContainer,
  GridRenderCellParams,
  GridRowsProp,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridValueFormatterParams,
  useGridApiRef
} from '@mui/x-data-grid-pro'
import { FC, useContext, useEffect, useMemo, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { AppRoutes } from '../../models'
import { AppContext } from '../../providers/AppProvider/AppProvider'
import {
  useLazyListVehicleReminderQuery,
  useListVehicleReminderQuery
} from '../../redux/api/vehicleApi'
import { CustomerEntry, VehicleReminder } from '../../sdk'
import { describeVehicle } from '../../utils/describeVehicle/describeVehicle'
import { formatName } from '../../utils/formatCustomerName/formatCustomerName'
import formatMdyDate from '../../utils/formatMdyDate/formatMdyDate'
import { AutomatedSendReminderContainer } from './AutomatedSendReminderContainer'
import { BatchSendReminderDialog } from './BatchSendReminderDialog'
import { SendReminderDialog } from './SendReminderDialog'

export const dateOnly = (date: Date | undefined): Date | undefined => {
  if (date) {
    return new Date(date.setHours(0, 0, 0, 0))
  }
  return date
}

export const getEarliestReminderDate = (
  reminder: VehicleReminder
): Date | undefined => {
  const inspectionDate = dateOnly(
    reminder.vehicleEntry?.info?.nextInspectionDate
  )
  const oilServiceDate = dateOnly(
    reminder.vehicleEntry?.info?.nextOilServiceDate
  )

  if (inspectionDate && oilServiceDate) {
    return inspectionDate < oilServiceDate ? inspectionDate : oilServiceDate
  } else if (inspectionDate) {
    return inspectionDate
  } else {
    return oilServiceDate
  }
}

const PAGE_SIZE = 1000

const CustomToolbar = ({
  batchSend,
  automatedSend,
  disabled
}: {
  batchSend: () => void
  automatedSend: () => void
  disabled: boolean
}) => {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      <div style={{ flexGrow: 1 }} />
      <Button
        size="small"
        variant="contained"
        onClick={batchSend}
        disabled={disabled}>
        Batch Send
      </Button>
      <Button
        size="small"
        variant="contained"
        onClick={automatedSend}
        disabled={disabled}>
        Scheduled Send
      </Button>
    </GridToolbarContainer>
  )
}

const CustomFooter = ({ reachedEnd }: { reachedEnd: boolean }) => {
  return (
    <GridFooterContainer>
      <Box pl={2}>
        {reachedEnd ? (
          <Typography>All Results Loaded</Typography>
        ) : (
          <Typography>Loading Remaining Rows...</Typography>
        )}
      </Box>
      <div style={{ flexGrow: 1 }} />
      <GridFooter />
    </GridFooterContainer>
  )
}

const CustomFooterAdd = () => {
  return <CustomFooter reachedEnd={false} />
}

const CustomFooterAll = () => {
  return <CustomFooter reachedEnd={true} />
}

const VehicleReminderList: FC = () => {
  const navigate = useNavigate()
  const gridApiRef = useGridApiRef()

  const { gridBoxHeight } = useContext(AppContext)

  const { accessToken, shopId } = useAccessTokenAndShopId()
  const [triggerSearch] = useLazyListVehicleReminderQuery()

  // fetch initial entries and display first
  const { data: initial, isLoading } = useListVehicleReminderQuery({
    authorization: accessToken,
    shop: shopId,
    numResults: PAGE_SIZE
  })

  // slice to solve vehicleEntry with multiple customerEntry issue
  // see rows for more info
  const initialVehicleReminderList = useMemo(
    () => initial?.slice(0, initial.length - 1) || [],
    [initial]
  )

  const [remainingVehicleReminderList, setRemainingVehicleReminderList] =
    useState<VehicleReminder[]>([])

  useEffect(() => {
    const fetchData = async () => {
      // only triggered when initialVehicleReminderList is changed
      if (initialVehicleReminderList.length > 0) {
        let remaining: VehicleReminder[] = []

        // fetch remaining entries and add only after all fetched in background
        while (true) {
          // note that initialVehicleReminderList length is at least 1 here
          // note that getEarliestReminderDate is guaranteed to be defined because of backend sql
          const lastDate =
            remaining.length > 0
              ? getEarliestReminderDate(
                  remaining[remaining.length - 1]
                )!.toISOString()
              : getEarliestReminderDate(
                  initialVehicleReminderList[
                    initialVehicleReminderList.length - 1
                  ]
                )!.toISOString()
          const lastTimestamp =
            remaining.length > 0
              ? remaining[
                  remaining.length - 1
                ].vehicleEntry?.created!.toISOString()
              : initialVehicleReminderList[
                  initialVehicleReminderList.length - 1
                ].vehicleEntry?.created!.toISOString()

          const next = await triggerSearch({
            authorization: accessToken,
            shop: shopId,
            lastDate,
            lastTimestamp,
            numResults: PAGE_SIZE
          }).unwrap()

          if (next.length >= 2) {
            // slice to solve vehicleEntry with multiple customerEntry issue
            // see rows for more info
            remaining = [...remaining, ...next.slice(0, next.length - 1)]
          } else {
            remaining = [...remaining, ...next]

            break
          }
        }

        setRemainingVehicleReminderList(remaining)
      }
    }

    fetchData()
  }, [accessToken, initialVehicleReminderList, shopId, triggerSearch])

  const [reminder, setReminder] = useState<VehicleReminder | undefined>(
    undefined
  )
  const [sent, setSent] = useState<string[]>([])

  const columns = useMemo<GridColDef[]>(() => {
    return [
      {
        field: 'ymm',
        headerName: 'YMM',
        flex: 2
      },
      {
        field: 'customers',
        headerName: 'Customers',
        flex: 2
      },
      {
        field: 'inspection',
        headerName: 'Next Inspection Date',
        flex: 1,
        type: 'date',
        valueFormatter: (params: GridValueFormatterParams) =>
          params.value ? formatMdyDate(params.value as Date) : ''
      },
      {
        field: 'oilService',
        headerName: 'Next Oil Service Date',
        flex: 1,
        type: 'date',
        valueFormatter: (params: GridValueFormatterParams) =>
          params.value ? formatMdyDate(params.value as Date) : ''
      },
      {
        field: 'invoice',
        headerName: 'Last Closed Invoice Date',
        flex: 1,
        type: 'date',
        valueFormatter: (params: GridValueFormatterParams) =>
          params.value ? formatMdyDate(params.value as Date) : ''
      },
      {
        field: 'reminder',
        headerName: 'Last Reminder Date',
        flex: 1,
        type: 'date',
        valueFormatter: (params: GridValueFormatterParams) =>
          params.value ? formatMdyDate(params.value as Date) : ''
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Status',
        headerAlign: 'left' as GridAlignment,
        align: 'left' as GridAlignment,
        width: 300,
        getActions: (params: GridRenderCellParams) => {
          return [
            <Button
              key="sendReminder"
              variant="outlined"
              disabled={params.row.isSent}
              sx={{ transform: 'scale(0.8)' }}
              onClick={() =>
                setReminder(params.row.vehicleReminder as VehicleReminder)
              }>
              Send Reminder
            </Button>,
            params.row.isSent && (
              <Chip
                key="reminderSent"
                label="Reminder Sent"
                sx={{
                  bgcolor: 'lightcyan',
                  '&.MuiChip-root:hover': {
                    bgcolor: 'lightblue'
                  }
                }}
              />
            )
          ].filter(Boolean)
        }
      }
    ]
  }, [])

  const allVehicleReminders = useMemo(() => {
    // ensure that final list is unique
    // fetch goes by number of rows and for one vehicleEntry it can have multiple rows because of multiple customerEntry
    // if fetch cuts off the remaining customerEntry for one vehicleEntry, that vehicleWithCustomers would be refetched in the next call
    // need to update to use the latest vehicleWithCustomers and remove duplicates
    const map = new Map<string, VehicleReminder>()
    for (const item of initialVehicleReminderList) {
      map.set(item.vehicleEntry!.id!, item)
    }
    for (const item of remainingVehicleReminderList) {
      map.set(item.vehicleEntry!.id!, item)
    }
    return Array.from(map.values())
  }, [initialVehicleReminderList, remainingVehicleReminderList])

  const rows: GridRowsProp = useMemo(() => {
    return (allVehicleReminders || []).map((vr) => ({
      id: vr.vehicleEntry?.id,
      ymm: describeVehicle(vr.vehicleEntry?.info),
      inspection: vr.vehicleEntry?.info?.nextInspectionDate,
      oilService: vr.vehicleEntry?.info?.nextOilServiceDate,
      customers: vr.customerEntries
        ?.map((customerEntry: CustomerEntry) =>
          formatName(customerEntry.information)
        )
        .join(', '),
      vehicleReminder: vr,
      isSent: sent.includes(vr.vehicleEntry!.id!),
      invoice:
        vr.closedRepairOrderEntries && vr.closedRepairOrderEntries?.length >= 1
          ? vr.closedRepairOrderEntries[0].info?.closedOn
          : undefined,
      reminder:
        vr.vehicleReminderMessageEntries &&
        vr.vehicleReminderMessageEntries?.length >= 1
          ? vr.vehicleReminderMessageEntries[0].createdAt
          : undefined
    }))
  }, [allVehicleReminders, sent])

  useEffect(() => {
    const onRowClick: GridEventListener<'rowClick'> = ({ id }) => {
      const vehicleUrl = generatePath(AppRoutes.VEHICLE_DETAILS, {
        id: id as string
      })
      navigate(vehicleUrl)
    }

    return gridApiRef.current.subscribeEvent('rowClick', onRowClick)
  }, [gridApiRef, navigate])

  const [openBatchSendReminderDialog, setOpenBatchSendReminderDialog] =
    useState<boolean>(false)
  const [
    openAutomatedSendReminderContainer,
    setOpenAutomatedSendReminderContainer
  ] = useState<boolean>(false)

  return (
    <>
      {openAutomatedSendReminderContainer && (
        <AutomatedSendReminderContainer
          handleClose={() => setOpenAutomatedSendReminderContainer(false)}
          vehicleReminders={allVehicleReminders}
        />
      )}

      {openBatchSendReminderDialog && (
        <BatchSendReminderDialog
          open={openBatchSendReminderDialog}
          handleClose={() => setOpenBatchSendReminderDialog(false)}
          vehicleReminders={allVehicleReminders}
          handleSent={(ids) => {
            setSent([...sent, ...ids])
          }}
        />
      )}

      {reminder && (
        <SendReminderDialog
          vehicleReminder={reminder!}
          open={!!reminder}
          handleClose={() => setReminder(undefined)}
          handleSent={(id) => {
            setSent([...sent, id])
            setReminder(undefined)
          }}
        />
      )}

      <Box sx={{ height: gridBoxHeight, width: '100%' }}>
        <DataGridPro
          apiRef={gridApiRef}
          density="compact"
          rows={rows}
          columns={columns}
          loading={isLoading}
          slots={{
            toolbar: () => (
              <CustomToolbar
                batchSend={() => setOpenBatchSendReminderDialog(true)}
                automatedSend={() =>
                  setOpenAutomatedSendReminderContainer(true)
                }
                disabled={!(remainingVehicleReminderList.length >= 1)}
              />
            ),
            footer:
              remainingVehicleReminderList.length >= 1
                ? CustomFooterAll
                : CustomFooterAdd
          }}
          sx={{
            '& .MuiDataGrid-row:hover': {
              cursor: 'pointer'
            },
            border: '0px'
          }}
        />
      </Box>
    </>
  )
}

export default VehicleReminderList
